/**
 * @fileoverview JobsIndicator component
 *
 * A compact badge for the topbar that shows:
 * - Count of active + queued jobs
 * - Spinning animation when jobs are running
 * - Click handler to open jobs panel
 *
 * Hidden when no jobs are active or queued.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { useJobsStore } from '../../store/jobsStore';

interface JobsIndicatorProps {
    /** Handler called when indicator is clicked */
    onClick: () => void;
}

export const JobsIndicator: React.FC<JobsIndicatorProps> = ({ onClick }) => {
    const getJobCounts = useJobsStore((state) => state.getJobCounts);
    const getActiveJobs = useJobsStore((state) => state.getActiveJobs);

    const counts = getJobCounts();
    const activeJobs = getActiveJobs();

    // Total count of active + queued jobs
    const totalPending = counts.active + counts.queued;

    // Check if any job is currently running (not just queued)
    const hasRunning = activeJobs.some((job) => job.status === 'RUNNING');

    // Don't render if no pending jobs
    if (totalPending === 0) {
        return null;
    }

    return (
        <button
            onClick={onClick}
            aria-label={`${totalPending} jobs`}
            className="relative flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
                bg-blue-600/20 text-blue-400 border border-blue-500/30
                hover:bg-blue-600/30 transition-colors"
        >
            <FontAwesomeIcon
                icon={faGear}
                className={`w-3 h-3 ${hasRunning ? 'animate-spin' : ''}`}
            />
            <span>{totalPending}</span>
        </button>
    );
};
